import type { Vector3 } from '@technology/web3d';
import {
  chainageToCumDistance,
  chainageToWeb3dChainage,
  web3dChainageToChainage,
} from '../trim-bim/trimbim-alignment';
import type {
  SectionChainageData,
  TrajectoryMetadata,
} from '../trim-bim/trimbim-alignment.types';
import { VALUES_PER_SEGMENT } from '../trim-bim/trimbim-segment-vertices';
import type {
  MissionPathRuns,
  MissionRunRange,
  StationPathEntry,
  StationPathIndexInput,
  StationPathIndexResult,
  StationPathLocation,
} from './station-path-index.types';

/** A station closer than this to several path passes keeps the pass nearest its predecessor. */
export const ON_PATH_TOLERANCE_METRES = 1;

const Y = 1;
const Z = 2;
const END = 3;

interface RunSection {
  readonly section: SectionChainageData;
  readonly vertices: Float64Array;
  /** Fractional segment index range covered by the run, as `web3dChainage`. */
  readonly fromChainage: number;
  readonly toChainage: number;
}

interface Projection {
  readonly distanceSq: number;
  readonly runSection: RunSection;
  readonly web3dChainage: number;
}

export function buildStationPathIndex(
  input: StationPathIndexInput
): StationPathIndexResult {
  const runs = indexRuns(input.missions);
  const stationsByRun = groupStationsByRun(input, runs);

  const entries: StationPathEntry[] = [];
  const computed = new Map<string, StationPathLocation>();
  const pathsMissingGeometry = new Set<string>();

  for (const [runId, stationIndices] of stationsByRun) {
    const runEntry = runs.get(runId);
    if (!runEntry) {
      continue;
    }
    const { mission, run } = runEntry;
    const metadata = mission.pathId
      ? input.pathMetadata(mission.pathId)
      : undefined;
    let runSections: readonly RunSection[] | null | undefined;
    let previousDistance: number | undefined;

    for (const stationIndex of stationIndices) {
      const station = input.stations[stationIndex];
      let location = input.cache.get(station.stationKey);

      if (!location && metadata && mission.pathId) {
        runSections ??= collectRunSections(metadata, run);
        if (runSections === null) {
          pathsMissingGeometry.add(mission.pathId);
        } else {
          location = locateStation(
            station.position,
            runSections,
            previousDistance
          );
          computed.set(station.stationKey, location);
        }
      }

      previousDistance = location?.pathDistance ?? previousDistance;
      entries.push({
        ...location,
        stationKey: station.stationKey,
        stationIndex,
        missionId: mission.missionId,
        runId,
        pathId: mission.pathId,
      });
    }
  }

  entries.sort((a, b) => a.stationIndex - b.stationIndex);
  return { entries, computed, pathsMissingGeometry };
}

function indexRuns(
  missions: readonly MissionPathRuns[]
): Map<string, { mission: MissionPathRuns; run: MissionRunRange }> {
  const runs = new Map<
    string,
    { mission: MissionPathRuns; run: MissionRunRange }
  >();
  for (const mission of missions) {
    for (const run of mission.runs) {
      runs.set(run.runId, { mission, run });
    }
  }
  return runs;
}

function groupStationsByRun(
  input: StationPathIndexInput,
  runs: ReadonlyMap<string, unknown>
): Map<string, number[]> {
  const stationsByRun = new Map<string, number[]>();
  input.stations.forEach((station, index) => {
    if (!station.runId || !runs.has(station.runId)) {
      return;
    }
    const indices = stationsByRun.get(station.runId) ?? [];
    indices.push(index);
    stationsByRun.set(station.runId, indices);
  });
  return stationsByRun;
}

/** Returns `null` when a section the run needs has released its vertices. */
function collectRunSections(
  metadata: TrajectoryMetadata,
  run: MissionRunRange
): readonly RunSection[] | null {
  const runSections: RunSection[] = [];
  for (const section of metadata.sectionChainages ?? []) {
    if (
      section.endStation <= run.beginStation ||
      section.beginStation >= run.endStation
    ) {
      continue;
    }
    if (!section.segmentVertices) {
      return null;
    }

    const segmentCount = section.segmentVertices.length / VALUES_PER_SEGMENT;
    const fromChainage =
      chainageToWeb3dChainage(
        Math.max(run.beginStation, section.beginStation),
        section
      ) ?? 0;
    const toChainage =
      chainageToWeb3dChainage(
        Math.min(run.endStation, section.endStation),
        section
      ) ?? segmentCount;
    runSections.push({
      section,
      vertices: section.segmentVertices,
      fromChainage,
      toChainage,
    });
  }
  return runSections;
}

function locateStation(
  position: Vector3,
  runSections: readonly RunSection[],
  previousDistance: number | undefined
): StationPathLocation {
  let nearest: Projection | undefined;
  const passes: Projection[] = [];
  for (const runSection of runSections) {
    const scan = scanRunSection(position, runSection);
    if (
      scan.nearest &&
      (!nearest || scan.nearest.distanceSq < nearest.distanceSq)
    ) {
      nearest = scan.nearest;
    }
    passes.push(...scan.passes);
  }
  if (!nearest) {
    return {};
  }
  // The first station of a run was captured at its start, so it takes the
  // earliest pass.
  const chosen = closestToPrevious(passes, previousDistance);
  return toLocation(chosen ?? nearest);
}

function closestToPrevious(
  passes: readonly Projection[],
  previousDistance: number | undefined
): Projection | undefined {
  let best: Projection | undefined;
  let bestGap = Infinity;
  for (const pass of passes) {
    const distance = toPathDistance(pass);
    if (distance === undefined) {
      continue;
    }
    const gap =
      previousDistance === undefined
        ? distance
        : Math.abs(distance - previousDistance);
    if (gap < bestGap) {
      best = pass;
      bestGap = gap;
    }
  }
  return best;
}

/**
 * Returns the nearest projection, and one projection per pass within the
 * on-path tolerance: the nearest of each run of consecutive close segments.
 */
function scanRunSection(
  position: Vector3,
  runSection: RunSection
): { nearest: Projection | undefined; passes: Projection[] } {
  const { vertices, fromChainage, toChainage } = runSection;
  const segmentCount = vertices.length / VALUES_PER_SEGMENT;
  const first = Math.max(0, Math.floor(fromChainage));
  const last = Math.min(segmentCount - 1, Math.ceil(toChainage) - 1);
  const toleranceSq = ON_PATH_TOLERANCE_METRES * ON_PATH_TOLERANCE_METRES;

  let nearest: Projection | undefined;
  const passes: Projection[] = [];
  let pass: Projection | undefined;

  for (let segment = first; segment <= last; segment++) {
    const o = segment * VALUES_PER_SEGMENT;
    const sx = vertices[o];
    const sy = vertices[o + Y];
    const sz = vertices[o + Z];
    const dx = vertices[o + END] - sx;
    const dy = vertices[o + END + Y] - sy;
    const dz = vertices[o + END + Z] - sz;
    const lengthSq = dx * dx + dy * dy + dz * dz;
    const minT = segment === first ? Math.max(0, fromChainage - segment) : 0;
    const maxT = segment === last ? Math.min(1, toChainage - segment) : 1;
    const rawT =
      lengthSq > 0
        ? ((position.x - sx) * dx +
            (position.y - sy) * dy +
            (position.z - sz) * dz) /
          lengthSq
        : 0;
    const t = Math.min(Math.max(rawT, minT), maxT);
    const ex = sx + t * dx - position.x;
    const ey = sy + t * dy - position.y;
    const ez = sz + t * dz - position.z;
    const distanceSq = ex * ex + ey * ey + ez * ez;

    if (!nearest || distanceSq < nearest.distanceSq) {
      nearest = { distanceSq, runSection, web3dChainage: segment + t };
    }
    if (distanceSq > toleranceSq) {
      if (pass) {
        passes.push(pass);
        pass = undefined;
      }
      continue;
    }
    if (!pass || distanceSq < pass.distanceSq) {
      pass = { distanceSq, runSection, web3dChainage: segment + t };
    }
  }
  if (pass) {
    passes.push(pass);
  }
  return { nearest, passes };
}

function toPathDistance(projection: Projection): number | undefined {
  const { section } = projection.runSection;
  const chainage = web3dChainageToChainage(projection.web3dChainage, section);
  const cumDistance = chainage
    ? chainageToCumDistance(chainage, section)
    : undefined;
  return cumDistance === undefined
    ? undefined
    : section.beginDistance + cumDistance;
}

function toLocation(projection: Projection): StationPathLocation {
  return {
    pathDistance: toPathDistance(projection),
    offPathDistance: Math.sqrt(projection.distanceSq),
  };
}
